import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate, useParams, useRouterState } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { useTranslations } from '@/i18n/TranslationsProvider';
import { translateSportsLabel } from '@/i18n/sports-label';
import { cn } from '@/lib/cn';
import { Icon, SportIcon } from '@/ui';
import { sportsMenuFullQuery } from '@/api/queries';
import {
  getCategoryFlag,
  getTournamentOrCategoryBadge,
  isFlagBadgeSrc,
  resolveMenuExpansion,
  sportChangeTarget,
  sportIcon,
  toSlug,
} from './sports-menu-utils';

export interface SideNavProps {
  className?: string;
  /** Opens the shared sports search overlay. */
  onOpenSearch?: () => void;
}

/** The 240px sports rail. Loads sports from the sAPI20 menu/full endpoint. */
export function SideNav({ className, onOpenSearch }: SideNavProps) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const params = useParams({ strict: false });
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  const sportFromRoute = typeof params.sport === 'string' ? params.sport : undefined;
  const categoryFromRoute = typeof params.category === 'string' ? params.category : undefined;
  const tournamentFromRoute =
    typeof params.tournament === 'string' ? params.tournament : undefined;
  const { selectedLanguage, isLoadingTranslations } = useTranslations();
  const { data: sports, isPending, isError } = useQuery(sportsMenuFullQuery());
  const [expandedSportId, setExpandedSportId] = useState<number | null>(null);
  const [expandedCategoryBySport, setExpandedCategoryBySport] = useState<Record<number, number | null>>({});
  const sportsTranslationsReady =
    selectedLanguage === 'en' || i18n.hasResourceBundle(selectedLanguage, 'sports');
  const shouldHoldForTranslations = !sportsTranslationsReady && isLoadingTranslations;
  const labelFor = (id: number, fallbackLabel: string) =>
    translateSportsLabel(i18n, t, id, fallbackLabel);

  useEffect(() => {
    const expansion = resolveMenuExpansion(
      sports,
      sportFromRoute,
      categoryFromRoute,
      tournamentFromRoute,
    );
    if (!expansion) return;

    setExpandedSportId(expansion.sportId);
    if (expansion.categoryId != null) {
      setExpandedCategoryBySport((current) => ({
        ...current,
        [expansion.sportId]: expansion.categoryId,
      }));
    }
  }, [sports, sportFromRoute, categoryFromRoute, tournamentFromRoute]);

  const selectSport = (sportId: number, sportSlug: string) => {
    const alreadyActive = sportSlug === sportFromRoute;
    const alreadyExpanded = expandedSportId === sportId;
    const onCategoryOrTournament = Boolean(categoryFromRoute || tournamentFromRoute);

    if (alreadyActive && alreadyExpanded && !onCategoryOrTournament) {
      setExpandedSportId(null);
      return;
    }

    setExpandedSportId(sportId);
    void navigate(sportChangeTarget(pathname, sportSlug));
  };

  const selectCategory = (sportId: number, categoryId: number, sportSlug: string, categorySlug: string) => {
    setExpandedSportId(sportId);
    setExpandedCategoryBySport((current) => ({
      ...current,
      [sportId]: categoryId,
    }));
    void navigate({
      to: '/sports/$sport/$category',
      params: { sport: sportSlug, category: categorySlug },
    });
  };

  return (
    <nav
      aria-label={t('Sports', { defaultValue: 'Sports' })}
      className={cn('flex w-60 shrink-0 flex-col gap-2 px-3 py-4', className)}
    >
      {/* Search field — opens SportsSearchOverlay */}
      <button
        type="button"
        onClick={onOpenSearch}
        className={cn(
          'flex h-11 w-full shrink-0 items-center gap-2.5 rounded-md border border-border-default bg-bg-surface',
          'px-4 text-sm text-text-secondary',
          'hover:border-border-focus focus-visible:border-border-focus focus-visible:outline-none',
        )}
      >
        <Icon name="search" size={16} className="shrink-0" />
        <span className="truncate">
          {t('Search events', { defaultValue: 'Search events' })}
        </span>
      </button>

      {/* Section label */}
      <p className="text-[11px] font-bold whitespace-nowrap text-text-secondary">
        {t('Sports', { defaultValue: 'SPORTS' }).toUpperCase()}
      </p>

      {(isPending || shouldHoldForTranslations) && (
        <ul className="flex flex-col gap-2">
          {Array.from({ length: 7 }).map((_, i) => (
            <li key={i} className="h-10 animate-pulse rounded-md bg-bg-elevated" />
          ))}
        </ul>
      )}

      {isError && (
        <p className="px-2 text-body-s text-text-secondary">
          {t('Could not load sports menu.', { defaultValue: 'Could not load sports menu.' })}
        </p>
      )}

      {sports && !shouldHoldForTranslations && (
        <ul className="flex flex-col gap-2">
          {[...sports]
            .sort((a, b) => a.Position - b.Position)
            .map((sport) => {
              const sportLabel = labelFor(sport.Id, sport.Name);
              const sportSlug = toSlug(sport.Name);
              const sportExpanded = expandedSportId === sport.Id;
              const sportActive = sportSlug === sportFromRoute;
              const categories = [...sport.Categories].sort((a, b) => a.Position - b.Position);
              // TODO: remove fallback once the API returns correct Count on SportMenuItem
              const sportCount =
                sport.Count ||
                sport.Categories.flatMap((c) => c.Tournaments).reduce((sum, t) => sum + t.Count, 0);
              return (
                <li key={sport.Id}>
                  {/* Sport row */}
                  <button
                    type="button"
                    onClick={() => selectSport(sport.Id, sportSlug)}
                    className={cn(
                      'flex h-10 w-full items-center gap-2.5 overflow-clip rounded-lg bg-bg-elevated px-2.5 text-left',
                      'hover:bg-bg-surface',
                    )}
                  >
                    <SportIcon name={sportIcon(sportSlug)} size={20} />
                    <span
                      className={cn(
                        'min-w-0 flex-1 truncate text-[16px] leading-none',
                        sportActive ? 'font-bold text-text-on-action' : 'text-text-primary',
                      )}
                    >
                      {sportLabel}
                    </span>
                    <span
                      className={cn(
                        'shrink-0 text-[12px] tabular-nums',
                        sportActive ? 'text-text-on-action' : 'text-text-secondary',
                      )}
                    >
                      {sportCount}
                    </span>
                  </button>

                  {/* Categories accordion */}
                  <div
                    className={cn(
                      'grid transition-[grid-template-rows] duration-200 ease-out',
                      sportExpanded ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]',
                    )}
                  >
                    <div className="min-h-0 overflow-hidden">
                      <ul className="flex flex-col gap-1 pt-1">
                        {categories.map((category) => {
                          const categoryLabel = labelFor(category.Id, category.Name);
                          const categorySlug = toSlug(category.Name);
                          const categoryExpanded = expandedCategoryBySport[sport.Id] === category.Id;
                          const categoryActive =
                            sportActive && categorySlug === categoryFromRoute;
                          const tournaments = [...category.Tournaments].sort(
                            (a, b) => a.Position - b.Position,
                          );
                          const isTopLeagues = category.Name.toLowerCase().includes('top league');
                          const flagSrc = getCategoryFlag(category.Name);
                          const categoryCount = category.Tournaments.reduce((sum, t) => sum + t.Count, 0);

                          return (
                            <li key={category.Id}>
                              {/* Category row */}
                              <button
                                type="button"
                                onClick={() =>
                                  selectCategory(sport.Id, category.Id, sportSlug, categorySlug)
                                }
                                className={cn(
                                  'flex h-8 w-full items-center gap-1.5 p-1.5 text-left',
                                  categoryActive
                                    ? 'bg-action-primary-hover text-text-on-action'
                                    : 'bg-bg-surface text-text-primary hover:bg-bg-elevated',
                                )}
                              >
                                {/* Chevron left */}
                                <Icon
                                  name="arrow-right"
                                  size={8}
                                  className={cn(
                                    'shrink-0 transition-transform duration-200',
                                    categoryActive ? 'text-text-on-action' : 'text-icon-muted',
                                    categoryExpanded && 'rotate-90',
                                  )}
                                />
                                {/* Icon: topleagues special icon, flag, or fallback */}
                                {isTopLeagues ? (
                                  <Icon
                                    name="topleagues"
                                    size={18}
                                    className={cn(
                                      'shrink-0',
                                      categoryActive ? 'text-text-on-action' : 'text-icon-muted',
                                    )}
                                  />
                                ) : flagSrc ? (
                                  <span className="flex size-5 shrink-0 items-center justify-center overflow-clip rounded-full">
                                    <img
                                      src={flagSrc}
                                      alt={categoryLabel}
                                      className="size-full object-cover"
                                    />
                                  </span>
                                ) : (
                                  <span
                                    className={cn(
                                      'flex size-5 shrink-0 items-center justify-center overflow-clip rounded-full',
                                      categoryActive ? 'bg-bg-surface/20' : 'bg-bg-elevated',
                                    )}
                                  >
                                    <span
                                      className={cn(
                                        'text-[7px] font-bold leading-none',
                                        categoryActive ? 'text-text-on-action' : 'text-text-secondary',
                                      )}
                                    >
                                      {categoryLabel.slice(0, 2).toUpperCase()}
                                    </span>
                                  </span>
                                )}
                                <span className="min-w-0 flex-1 truncate text-[14px] leading-4">
                                  {categoryLabel}
                                </span>
                                <span
                                  className={cn(
                                    'shrink-0 text-[10px] leading-4 tabular-nums',
                                    categoryActive ? 'text-text-on-action' : 'text-text-secondary',
                                  )}
                                >
                                  {categoryCount}
                                </span>
                              </button>

                              {/* Tournaments accordion */}
                              <div
                                className={cn(
                                  'grid transition-[grid-template-rows] duration-200 ease-out',
                                  categoryExpanded ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]',
                                )}
                              >
                                <div className="min-h-0 overflow-hidden">
                                  <ul className="flex flex-col gap-1 pt-1">
                                    {tournaments.map((tournament) => {
                                      const tournamentLabel = labelFor(tournament.Id, tournament.Name);
                                      const tournamentSlug = toSlug(tournament.Name);
                                      const tournamentActive =
                                        categoryActive && tournamentSlug === tournamentFromRoute;
                                      const crestSrc = getTournamentOrCategoryBadge(
                                        tournament.Name,
                                        category.Name,
                                      );
                                      return (
                                        <li key={tournament.Id}>
                                          <Link
                                            to="/sports/$sport/$category/$tournament"
                                            params={{
                                              sport: sportSlug,
                                              category: categorySlug,
                                              tournament: tournamentSlug,
                                            }}
                                            className={cn(
                                              'flex h-8 w-full items-center gap-1.5 pl-4 pr-1.5 py-1.5',
                                              tournamentActive
                                                ? 'bg-action-primary-hover text-text-on-action'
                                                : 'bg-bg-canvas text-text-primary hover:bg-bg-surface',
                                            )}
                                          >
                                            {/* Crest plate */}
                                            <span
                                              className={cn(
                                                'flex h-[20px] w-[20px] shrink-0 items-center justify-center overflow-clip rounded-full',
                                                crestSrc && !isFlagBadgeSrc(crestSrc) && 'bg-white',
                                              )}
                                            >
                                              {crestSrc ? (
                                                <img
                                                  src={crestSrc}
                                                  alt={tournamentLabel}
                                                  className={
                                                    isFlagBadgeSrc(crestSrc)
                                                      ? 'size-full object-cover'
                                                      : 'size-full object-contain p-0.5'
                                                  }
                                                />
                                              ) : (
                                                <span
                                                  className={cn(
                                                    'text-[6px] font-bold leading-none',
                                                    tournamentActive
                                                      ? 'text-text-on-action'
                                                      : 'text-gray-500',
                                                  )}
                                                >
                                                  {tournamentLabel.slice(0, 2).toUpperCase()}
                                                </span>
                                              )}
                                            </span>
                                            <span className="min-w-0 flex-1 truncate text-[13px] leading-4">
                                              {tournamentLabel}
                                            </span>
                                            <span
                                              className={cn(
                                                'shrink-0 text-[10px] leading-4 tabular-nums',
                                                tournamentActive
                                                  ? 'text-text-on-action'
                                                  : 'text-text-secondary',
                                              )}
                                            >
                                              {tournament.Count}
                                            </span>
                                          </Link>
                                        </li>
                                      );
                                    })}
                                  </ul>
                                </div>
                              </div>
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  </div>
                </li>
              );
            })}
        </ul>
      )}
    </nav>
  );
}
